import { apiClient } from './api'

export type ContractItem = {
  id: string
  name: string
  description: string | null
  resource_type: string
  unit_price: number
  currency: string
  billing_cycle: string
  created_at: string
}

/**
 * Última fatura do par (emissor → destinatário) do contrato — o FATURADO.
 *
 * Contraponto de `total_monthly_cost`, que é MRR de tabela: aqui entram
 * pró-rata de criação, estorno de deleção e resize. Ausente (`null`) quando o
 * par ainda não tem fatura nenhuma — a tela mostra "—", nunca R$ 0,00.
 */
export type LastInvoice = {
  invoice_id: string
  billing_period: string // "YYYY-MM"
  total_amount: number
  status: string
  currency: string
  /**
   * O backend já decidiu se o valor pode ser comparado com o MRR contratado — só
   * ele enxerga o par inteiro (a listagem é paginada) e a data de início do
   * contrato. A tela obedece; não recalcula.
   */
  comparable: boolean
  incomparable_reason: string | null
}

export type Contract = {
  id: string
  name: string
  type: string
  status: string
  billing_mode: string
  provider_id: string
  provider_name: string
  customer_id: string
  customer_name: string
  start_date: string
  end_date: string | null
  created_at: string
  items_count: number
  active_vms: number
  active_resources: Record<string, number>
  total_monthly_cost: number
  currency: string
  last_invoice?: LastInvoice | null
}

export type ContractDetail = {
  items: ContractItem[]
  terms?: Record<string, any>
} & Contract

export type ContractListResponse = {
  contracts: Contract[]
  total: number
  skip: number
  limit: number
}

// For reseller-specific contract response
export type ResellerContract = {
  id: string
  name: string
  type: string
  status: string
  billing_mode: string
  provider_id: string
  provider_name: string
  customer_id: string
  customer_name: string
  start_date: string
  end_date: string | null
  created_at: string
  items_count: number
  active_vms: number
  active_resources: Record<string, number>
  total_monthly_cost: number
  currency: string
}

export type RevenueStats = {
  mrr: number
  realized_revenue: number
  pending_amount: number
  overdue_amount: number
  total_contracts: number
  active_contracts: number
  currency: string
  period_start: string
  period_end: string
}

/** Linha de cobrança do período — da fatura fechada ou do parcial em curso. */
export type ContractBillingLine = {
  id: string | null // null nas linhas do parcial (ainda não persistidas)
  description: string
  resource_type: string | null
  source: string
  reference_type: string | null
  reference_id: string | null
  quantity: number
  unit_price: number | null
  amount: number // negativo em estorno pró-rata
  currency: string
  occurred_at: string | null
}

export type ContractBillingMonth = {
  invoice_id: string
  billing_period: string // "YYYY-MM"
  total_amount: number
  status: string
  currency: string
}

export type ContractBillingCurrentPeriod = {
  billing_period: string
  period_start: string
  period_end: string
  total_amount: number
  partial: boolean
  invoice_id: string | null
  line_items: ContractBillingLine[]
}

/**
 * Faturado do contrato: meses já fechados + parcial do mês corrente.
 *
 * `selected_period === null` significa que o par não tem NENHUMA fatura — a tela
 * mostra "—". `months` nunca assume "mês anterior": a fatura do mês fechado só
 * nasce dia 1 às 7h UTC, então o período vem sempre rotulado.
 */
export type ContractBilling = {
  contract_id: string
  issuer_org_id: string
  recipient_org_id: string
  currency: string
  /** Mesma régua da listagem (contrato encerrado, contrato do provedor, par com 2+ contratos). */
  comparable: boolean
  incomparable_reason: string | null
  /** Mês de início do contrato ("YYYY-MM"): mês faturado anterior é do contrato anterior. */
  contract_start_period: string | null
  current_period: ContractBillingCurrentPeriod
  months: ContractBillingMonth[]
  selected_period: string | null
  line_items: ContractBillingLine[]
}

export type CustomerSimple = {
  id: string
  name: string
  active_contracts: number
}

export const contractsApi = {
  // ========================================
  // End Customer Endpoints
  // ========================================

  // List contracts for end customer
  listCustomerContracts: (): Promise<ContractListResponse> => {
    return apiClient.get('/api/v1/end-customer/contracts')
  },

  // Get contract detail for end customer
  getCustomerContract: (contractId: string): Promise<ContractDetail> => {
    return apiClient.get(`/api/v1/end-customer/contracts/${contractId}`)
  },

  // ========================================
  // Reseller/Admin Endpoints
  // ========================================

  // Revenue stats (MRR, realized revenue, pending, overdue)
  getRevenueStats: (params?: { period?: string }): Promise<RevenueStats> => {
    const queryParams = new URLSearchParams()
    if (params?.period) queryParams.append('period', params.period)
    const queryString = queryParams.toString()
    const url = queryString ? `/api/v1/contracts/stats?${queryString}` : '/api/v1/contracts/stats'
    return apiClient.get(url)
  },

  // List unique customers with contracts (for resellers)
  listCustomers: (params?: { billing_mode_filter?: string }): Promise<CustomerSimple[]> => {
    const queryParams = new URLSearchParams()
    if (params?.billing_mode_filter)
      queryParams.append('billing_mode_filter', params.billing_mode_filter)

    const queryString = queryParams.toString()
    const url = queryString
      ? `/api/v1/contracts/customers?${queryString}`
      : '/api/v1/contracts/customers'

    return apiClient.get(url)
  },

  // List contracts (for resellers/admins)
  listContracts: (params?: {
    skip?: number
    limit?: number
    contract_type?: string
    status_filter?: string
    billing_mode_filter?: string
    customer_id?: string
    search?: string
  }): Promise<ContractListResponse> => {
    const queryParams = new URLSearchParams()
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.contract_type) queryParams.append('contract_type', params.contract_type)
    if (params?.status_filter) queryParams.append('status_filter', params.status_filter)
    if (params?.billing_mode_filter)
      queryParams.append('billing_mode_filter', params.billing_mode_filter)
    if (params?.customer_id) queryParams.append('customer_id', params.customer_id)
    if (params?.search) queryParams.append('search', params.search)

    const queryString = queryParams.toString()
    const url = queryString ? `/api/v1/contracts?${queryString}` : '/api/v1/contracts'

    return apiClient.get(url)
  },

  // Get contract detail (for resellers/admins)
  getContract: (contractId: string): Promise<ContractDetail> => {
    return apiClient.get(`/api/v1/contracts/${contractId}`)
  },

  // Faturado do contrato (histórico mensal + parcial do mês corrente)
  getContractBilling: (
    contractId: string,
    params?: {
      months?: number
      period?: string // "YYYY-MM" — detalha as linhas desse mês
    },
  ): Promise<ContractBilling> => {
    const queryParams = new URLSearchParams()
    if (params?.months !== undefined) queryParams.append('months', params.months.toString())
    if (params?.period) queryParams.append('period', params.period)

    const queryString = queryParams.toString()
    const url = queryString
      ? `/api/v1/contracts/${contractId}/billing?${queryString}`
      : `/api/v1/contracts/${contractId}/billing`

    return apiClient.get(url)
  },

  // List contracts for a specific reseller (CP → RS)
  // Used by Cloud Provider to see contracts with a specific reseller partner
  listContractsForReseller: (
    resellerId: string,
    params?: {
      skip?: number
      limit?: number
      status_filter?: string
    },
  ): Promise<ContractListResponse> => {
    const queryParams = new URLSearchParams()
    queryParams.append('customer_id', resellerId)
    queryParams.append('contract_type', 'provider_reseller')
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.status_filter) queryParams.append('status_filter', params.status_filter)

    return apiClient.get(`/api/v1/contracts?${queryParams}`)
  },
}

export default contractsApi
